import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  ShieldCheck,
  Smartphone,
  Lock,
  Trash2,
  Bell,
  Camera,
  HardDrive,
  Globe,
  Building2,
  Users,
  CreditCard,
  UserCheck,
  CheckCircle2,
  XCircle,
  Mail,
  MessageCircle,
  Copy,
  Check,
  ExternalLink,
  Cookie,
  AlertTriangle
} from 'lucide-react';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';

const LAST_UPDATED = '30 September 2026';

export const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedDeletionLink, setCopiedDeletionLink] = useState(false);
  const [copiedTemplate, setCopiedTemplate] = useState(false);

  // Check if opened with ?view=app or ?webview=true in Android WebView
  const isAppView = searchParams.get('view') === 'app' || searchParams.get('webview') === 'true';

  // Smoothly scroll to anchor hash (e.g. #hapus-akun) on page load or hash change
  useEffect(() => {
    const rawHash = (location.hash || window.location.hash || '').replace('#', '');
    if (rawHash) {
      const targetIds = [rawHash];
      if (rawHash === 'hapus-akun' || rawHash === 'delete-account') {
        targetIds.push('hapus-akun', 'box-hapus-akun', 'section-9');
      }

      const timer = setTimeout(() => {
        let el = null;
        for (const tid of targetIds) {
          el = document.getElementById(tid);
          if (el) break;
        }

        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          // Highlight card or section smoothly
          const targetBox = document.getElementById('box-hapus-akun') || el;
          if (targetBox) {
            targetBox.classList.add('ring-4', 'ring-amber-400', 'ring-offset-2', 'transition-all');
            setTimeout(() => {
              targetBox.classList.remove('ring-4', 'ring-amber-400', 'ring-offset-2');
            }, 3500);
          }
        }
      }, 150);

      return () => clearTimeout(timer);
    }
  }, [location.hash]);

  const copyPageUrl = () => {
    try {
      const url = window.location.origin + '/kebijakan-privasi';
      navigator.clipboard.writeText(url);
      setCopiedLink(true);
      toast({
        title: 'Tautan Berhasil Disalin',
        description: 'URL Kebijakan Privasi siap digunakan untuk Google Play Console atau dibagikan.',
      });
      setTimeout(() => setCopiedLink(false), 3000);
    } catch (e) {
      toast({
        title: 'Gagal menyalin',
        description: 'Silakan salin tautan manual dari address bar browser Anda.',
        variant: 'destructive',
      });
    }
  };

  const copyDeletionUrl = () => {
    try {
      const url = window.location.origin + '/privacy-policy#hapus-akun';
      navigator.clipboard.writeText(url);
      setCopiedDeletionLink(true);
      toast({
        title: 'Tautan Hapus Akun Disalin',
        description: 'URL resmi (https://.../privacy-policy#hapus-akun) telah disalin untuk Google Play Console.',
      });
      setTimeout(() => setCopiedDeletionLink(false), 3000);
    } catch (e) {
      toast({
        title: 'Gagal menyalin tautan',
        description: 'Silakan salin manual dari address bar browser.',
      });
    }
  };

  const copyDeletionTemplate = () => {
    const template = `Halo Admin IPL / Tim PT Nuratech Digital Nusantara,

Saya ingin mengajukan permohonan penghapusan akun dan data pribadi saya dari Aplikasi IPL.

Berikut data akun saya:
- Nama Lengkap: [Nama Anda]
- Username / Email Terdaftar: [Email / Username]
- Nama Perumahan / Kawasan: [Nama Kompleks Perumahan]
- Kode Unit: [Blok / Nomor Rumah]
- Alasan (Opsional): [Pindah rumah / Selesai masa tinggal]

Mohon diproses sesuai Kebijakan Privasi yang berlaku. Terima kasih.`;

    try {
      navigator.clipboard.writeText(template);
      setCopiedTemplate(true);
      toast({
        title: 'Format Template Disalin',
        description: 'Format pengajuan penghapusan akun telah disalin ke clipboard.',
      });
      setTimeout(() => setCopiedTemplate(false), 3000);
    } catch (e) {
      toast({
        title: 'Gagal menyalin format',
        description: 'Silakan gunakan tombol email atau WhatsApp langsung di bawah.',
      });
    }
  };

  const resetCookieConsent = () => {
    try {
      localStorage.removeItem('cookie-consent');
    } catch (e) {}
    toast({
      title: 'Pilihan cookie direset',
      description: 'Muat ulang halaman untuk menentukan pilihan cookie Anda kembali.',
    });
  };

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      try {
        window.history.replaceState(null, '', `#${id}`);
      } catch (e) {}
    }
  };

  return (
    <div className="min-h-screen bg-brand-50/70 pt-8 sm:pt-20 pb-16 font-sans">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
        {/* Navigation & Utilities */}
        {!isAppView && (
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6 sm:mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="hover:bg-white/80 text-foreground/80 hover:text-brand-800 -ml-2"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Kembali ke Beranda
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={copyPageUrl}
              className="bg-white/90 border-brand-200 text-brand-800 hover:bg-brand-100 hover:text-brand-900 shadow-sm"
              title="Salin tautan resmi untuk Google Play Console"
            >
              {copiedLink ? (
                <>
                  <Check className="mr-1.5 h-3.5 w-3.5 text-emerald-600" />
                  Tautan Disalin!
                </>
              ) : (
                <>
                  <Copy className="mr-1.5 h-3.5 w-3.5" />
                  Salin URL untuk Google Play
                </>
              )}
            </Button>
          </div>
        )}

        <motion.article
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="bg-card rounded-2xl sm:rounded-3xl border border-border/80 shadow-xl p-5 sm:p-10 md:p-12 text-foreground"
        >
          {/* Header */}
          <header className="border-b border-border/70 pb-8 mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-100 text-brand-800 text-xs sm:text-sm font-semibold mb-4 border border-brand-200">
              <Smartphone className="h-3.5 w-3.5" />
              Aplikasi Android &amp; Platform Digital
            </div>

            <h1 className="text-2xl sm:text-4xl font-extrabold text-foreground tracking-tight leading-tight mb-3">
              Kebijakan Privasi <span className="text-brand-700">Aplikasi IPL</span>
            </h1>

            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs sm:text-sm text-foreground/60">
              <p>
                <span className="font-semibold text-foreground/80">Pengembang:</span> PT Nuratech Digital Nusantara
              </p>
              <p>
                <span className="font-semibold text-foreground/80">Berlaku Sejak:</span> {LAST_UPDATED}
              </p>
            </div>

            <p className="mt-4 text-sm sm:text-base text-foreground/80 leading-relaxed">
              Kebijakan Privasi ini menjelaskan bagaimana <strong>PT Nuratech Digital Nusantara</strong> ("kami")
              mengumpulkan, menggunakan, menyimpan, membagikan, dan melindungi data pribadi Anda saat menggunakan{' '}
              <strong>aplikasi IPL</strong> ("Aplikasi"), yaitu aplikasi pengelolaan lingkungan perumahan untuk warga,
              pengelola, serta petugas keamanan dan kebersihan.
            </p>

            <div className="mt-4 p-4 rounded-xl bg-brand-50 border border-brand-200/80 text-xs sm:text-sm text-foreground/80 leading-relaxed flex items-start gap-3">
              <ShieldCheck className="h-5 w-5 text-brand-700 shrink-0 mt-0.5" />
              <div>
                <strong>Dasar Regulasi &amp; Kepatuhan:</strong> Dengan menggunakan Aplikasi, Anda menyatakan telah membaca
                dan memahami Kebijakan Privasi ini. Kebijakan ini disusun dengan mengacu pada{' '}
                <strong>Undang-Undang Nomor 27 Tahun 2022 tentang Pelindungan Data Pribadi (UU PDP)</strong> serta kebijakan
                keamanan data pengguna Google Play Store.
              </div>
            </div>
          </header>

          {/* Google Play Data Safety Quick Overview Badges */}
          <div className="mb-10 bg-slate-50/80 border border-slate-200 rounded-2xl p-4 sm:p-5">
            <h2 className="text-xs font-bold uppercase tracking-wider text-foreground/60 mb-3 flex items-center gap-1.5">
              <ShieldCheck className="h-4 w-4 text-emerald-600" />
              Ringkasan Keamanan Data Pengguna (Google Play Data Safety)
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div className="bg-white p-3 rounded-xl border border-slate-200/70 shadow-2xs">
                <div className="font-bold text-slate-800 flex items-center gap-1.5 mb-1">
                  <Lock className="h-3.5 w-3.5 text-emerald-600" /> Enkripsi HTTPS
                </div>
                <p className="text-slate-600">Seluruh lalu lintas data dienkripsi saat transit.</p>
              </div>

              <div className="bg-white p-3 rounded-xl border border-slate-200/70 shadow-2xs">
                <div className="font-bold text-slate-800 flex items-center gap-1.5 mb-1">
                  <CheckCircle2 className="h-3.5 w-3.5 text-brand-600" /> Tidak Jual Data
                </div>
                <p className="text-slate-600">Data pribadi tidak pernah dijual kepada pihak ketiga.</p>
              </div>

              <div className="bg-white p-3 rounded-xl border border-slate-200/70 shadow-2xs">
                <div className="font-bold text-slate-800 flex items-center gap-1.5 mb-1">
                  <CreditCard className="h-3.5 w-3.5 text-blue-600" /> Pembayaran Aman
                </div>
                <p className="text-slate-600">Diproses langsung via payment gateway resmi (DOKU).</p>
              </div>

              <div className="bg-white p-3 rounded-xl border border-slate-200/70 shadow-2xs">
                <div className="font-bold text-slate-800 flex items-center gap-1.5 mb-1">
                  <Trash2 className="h-3.5 w-3.5 text-amber-600" /> Hak Hapus Akun
                </div>
                <p className="text-slate-600">Tersedia mekanisme resmi permohonan hapus data &amp; akun.</p>
              </div>
            </div>
          </div>

          {/* Quick Jump Navigator */}
          <nav aria-label="Daftar Isi Kebijakan" className="mb-10">
            <h2 className="text-xs font-bold uppercase tracking-wider text-foreground/50 mb-3">Daftar Isi Cepat</h2>
            <div className="flex flex-wrap gap-2">
              {[
                { id: 'section-1', label: '1. Peran Pengelola' },
                { id: 'section-2', label: '2. Data Dikumpulkan' },
                { id: 'section-3', label: '3. Izin Android' },
                { id: 'section-4', label: '4. Penggunaan Data' },
                { id: 'section-5', label: '5. Dasar Pemrosesan' },
                { id: 'section-6', label: '6. Pihak Ketiga' },
                { id: 'section-7', label: '7. Keamanan Data' },
                { id: 'section-8', label: '8. Retensi Data' },
                { id: 'section-9', label: '9. Hak & Hapus Akun' },
                { id: 'section-10', label: '10. Data Anak' },
                { id: 'section-11', label: '11. Perubahan' },
                { id: 'section-12', label: '12. Kontak Resmi' },
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  type="button"
                  className="px-2.5 py-1 text-xs rounded-lg bg-brand-50 hover:bg-brand-100 text-brand-800 border border-brand-200 transition-colors"
                >
                  {item.label}
                </button>
              ))}
            </div>
          </nav>

          {/* 1. Peran Kami dan Pengelola Perumahan */}
          <section id="section-1" className="mb-10 scroll-mt-24">
            <h2 className="text-lg sm:text-xl font-bold text-foreground mb-3 flex items-center gap-2">
              <span className="flex items-center justify-center h-6 w-6 rounded-full bg-brand-700 text-white text-xs font-bold">1</span>
              Peran Kami dan Pengelola Perumahan
            </h2>
            <div className="space-y-3 text-sm sm:text-base text-foreground/80 leading-relaxed pl-8">
              <p>
                Aplikasi digunakan oleh pengelola perumahan, kawasan, atau lingkungan ("Pengelola") untuk melayani warganya.
                Akun warga dibuat dan dikelola oleh Pengelola.
              </p>
              <p>
                Pengelola menentukan tujuan penggunaan data warga di lingkungannya (misalnya penagihan iuran, pendataan warga,
                dan keamanan lingkungan). Dalam hal ini Pengelola bertindak sebagai <strong>pengendali data pribadi</strong>.
              </p>
              <p>
                Kami bertindak sebagai <strong>prosesor data</strong> yang menyediakan dan mengoperasikan platform Aplikasi, serta
                memproses data atas nama Pengelola sesuai kebijakan ini.
              </p>
              <p>
                Untuk permintaan tertentu terkait data warga, kami dapat meneruskannya kepada Pengelola atau berkoordinasi
                dengan Pengelola.
              </p>
            </div>
          </section>

          {/* 2. Data yang Kami Kumpulkan */}
          <section id="section-2" className="mb-10 scroll-mt-24">
            <h2 className="text-lg sm:text-xl font-bold text-foreground mb-3 flex items-center gap-2">
              <span className="flex items-center justify-center h-6 w-6 rounded-full bg-brand-700 text-white text-xs font-bold">2</span>
              Data yang Kami Kumpulkan
            </h2>

            <div className="space-y-4 text-sm sm:text-base text-foreground/80 leading-relaxed pl-8">
              <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                <h3 className="font-bold text-foreground mb-2 flex items-center gap-2">
                  <UserCheck className="h-4 w-4 text-brand-700" />
                  a. Data Akun dan Profil
                </h3>
                <ul className="list-disc pl-5 space-y-1 text-foreground/75 text-sm">
                  <li>Nama lengkap, username, alamat email, dan nomor telepon aktif.</li>
                  <li>Informasi unit/hunian (kode unit, nama perumahan/lingkungan) dan status sebagai pemilik, penghuni, atau penanggung tagihan.</li>
                  <li>Jenis akun (warga, petugas keamanan, atau petugas kebersihan) dan kata sandi (disimpan dalam bentuk terenkripsi satu arah di server).</li>
                </ul>
              </div>

              <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                <h3 className="font-bold text-foreground mb-2 flex items-center gap-2">
                  <Users className="h-4 w-4 text-brand-700" />
                  b. Data Keluarga
                </h3>
                <p className="text-foreground/75 text-sm">
                  Nama lengkap anggota keluarga, Nomor Induk Kependudukan (NIK) dan/atau Nomor Kartu Keluarga, jenis kelamin, hubungan dalam keluarga, nomor kontak darurat, dan foto anggota keluarga (jika diunggah oleh pengguna).
                </p>
              </div>

              <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                <h3 className="font-bold text-foreground mb-2 flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-brand-700" />
                  c. Data Layanan yang Anda Gunakan
                </h3>
                <ul className="list-disc pl-5 space-y-1.5 text-foreground/75 text-sm">
                  <li><strong>Tagihan dan pembayaran:</strong> data tagihan iuran, riwayat pembayaran, status transaksi, dan berkas/foto bukti transfer yang Anda unggah secara sukarela.</li>
                  <li><strong>Lapor tamu:</strong> nama tamu, tanggal kunjungan, keterangan menginap dan lama menginap, catatan kedatangan, serta foto KTP tamu (jika diunggah untuk verifikasi gerbang).</li>
                  <li><strong>Lapor masalah/keluhan:</strong> isi laporan, foto pendukung keluhan, status penanganan, dan riwayat penanganan pengurus.</li>
                  <li><strong>Izin renovasi:</strong> deskripsi pekerjaan renovasi, nama dan nomor telepon kontraktor/penanggung jawab, jumlah pekerja, data kendaraan material, dan dokumen pendukung.</li>
                  <li><strong>Pembuatan surat, pemesanan fasilitas, &amp; laporan bug:</strong> data yang Anda masukkan pada formulir permohonan terkait.</li>
                  <li><strong>Petugas keamanan dan kebersihan:</strong> jadwal penugasan dan catatan penyelesaian keluhan di lapangan.</li>
                </ul>
              </div>

              <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                <h3 className="font-bold text-foreground mb-2 flex items-center gap-2">
                  <Smartphone className="h-4 w-4 text-brand-700" />
                  d. Data Perangkat dan Teknis
                </h3>
                <ul className="list-disc pl-5 space-y-1 text-foreground/75 text-sm">
                  <li>Informasi spesifikasi perangkat (merek, model, sistem operasi, versi OS) serta versi Aplikasi IPL yang terpasang.</li>
                  <li>Token notifikasi perangkat (Firebase Cloud Messaging) untuk menyampaikan pemberitahuan iuran dan pengumuman secara tepat waktu.</li>
                  <li>Data teknis log saat Aplikasi berkomunikasi dengan server kami (waktu permintaan, kode respons/kesalahan) semata-mata untuk keperluan keamanan dan perbaikan crash sistem.</li>
                </ul>
              </div>

              <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                <h3 className="font-bold text-foreground mb-2 flex items-center gap-2">
                  <Globe className="h-4 w-4 text-brand-700" />
                  e. Data Interaksi Iklan Internal
                </h3>
                <p className="text-foreground/75 text-sm mb-2">
                  Aplikasi dapat menampilkan informasi promosi/iklan internal yang dikelola langsung oleh kami atau Pengelola perumahan. Kami hanya mencatat jumlah klik agregat pada informasi tersebut.
                </p>
                <p className="text-foreground/75 text-sm">
                  Aplikasi <strong>tidak menggunakan jaringan iklan pihak ketiga</strong>, <strong>tidak menggunakan ID iklan perangkat (Google Advertising ID)</strong>, dan <strong>tidak melacak aktivitas Anda</strong> di aplikasi atau situs web lain.
                </p>
              </div>

              {/* Data Safety Negative Disclosures (Crucial for Play Store Review) */}
              <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-xs sm:text-sm text-emerald-950 flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-emerald-700 shrink-0 mt-0.5" />
                <div>
                  <strong>Komitmen Tanpa Pengumpulan Data Sensitif Tambahan:</strong>
                  <br />
                  Aplikasi IPL <strong>TIDAK MENGUMPULKAN</strong> data lokasi presisi (GPS latar belakang/foreground), tidak membaca daftar kontak telepon perangkat Anda, tidak mengakses mikrofon/rekaman suara, dan tidak mengumpulkan data catatan kesehatan pribadi.
                </div>
              </div>
            </div>
          </section>

          {/* 3. Izin Perangkat yang Digunakan (Android Permissions) */}
          <section id="section-3" className="mb-10 scroll-mt-24">
            <h2 className="text-lg sm:text-xl font-bold text-foreground mb-3 flex items-center gap-2">
              <span className="flex items-center justify-center h-6 w-6 rounded-full bg-brand-700 text-white text-xs font-bold">3</span>
              Izin Perangkat yang Digunakan (Android Permissions)
            </h2>
            <div className="space-y-4 text-sm sm:text-base text-foreground/80 leading-relaxed pl-8">
              <p>
                Untuk menjalankan fungsinya pada sistem operasi Android, Aplikasi memerlukan beberapa izin akses yang diminta
                secara transparan saat fitur terkait digunakan:
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div className="p-3.5 rounded-xl border border-slate-200 bg-white flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-brand-100 text-brand-800 shrink-0">
                    <Bell className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-foreground">Notifikasi (POST_NOTIFICATIONS)</h4>
                    <p className="text-xs text-foreground/70 mt-1">
                      Memberitahukan tagihan bulanan baru, status verifikasi pembayaran, pengumuman warga, dan laporan tamu.
                    </p>
                  </div>
                </div>

                <div className="p-3.5 rounded-xl border border-slate-200 bg-white flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-brand-100 text-brand-800 shrink-0">
                    <Camera className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-foreground">Foto, Galeri, dan Berkas</h4>
                    <p className="text-xs text-foreground/70 mt-1">
                      Mengunggah bukti pembayaran, foto laporan keluhan, foto KTP tamu, serta menyimpan bukti kwitansi resmi ke galeri. Aplikasi hanya mengakses berkas yang Anda pilih secara spesifik.
                    </p>
                  </div>
                </div>

                <div className="p-3.5 rounded-xl border border-slate-200 bg-white flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-brand-100 text-brand-800 shrink-0">
                    <HardDrive className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-foreground">Penyimpanan (Khusus Android Versi Lama)</h4>
                    <p className="text-xs text-foreground/70 mt-1">
                      Hanya pada versi Android lama (Android 12 ke bawah) untuk keperluan mengunduh dan menyimpan dokumen atau kwitansi format PDF/gambar.
                    </p>
                  </div>
                </div>

                <div className="p-3.5 rounded-xl border border-slate-200 bg-white flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-brand-100 text-brand-800 shrink-0">
                    <Globe className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-foreground">Akses Internet (INTERNET)</h4>
                    <p className="text-xs text-foreground/70 mt-1">
                      Membuka sambungan HTTPS yang aman ke server IPL untuk sinkronisasi data transaksi dan laporan lingkungan.
                    </p>
                  </div>
                </div>
              </div>

              <p className="text-xs text-foreground/60 italic">
                * Anda dapat mencabut atau meninjau izin ini kapan saja melalui menu Pengaturan Aplikasi (Settings &gt; Apps &gt; IPL) di perangkat Android Anda. Jika izin dicabut, fitur yang membutuhkannya mungkin tidak dapat beroperasi secara maksimal.
              </p>
            </div>
          </section>

          {/* 4. Cara Kami Menggunakan Data */}
          <section id="section-4" className="mb-10 scroll-mt-24">
            <h2 className="text-lg sm:text-xl font-bold text-foreground mb-3 flex items-center gap-2">
              <span className="flex items-center justify-center h-6 w-6 rounded-full bg-brand-700 text-white text-xs font-bold">4</span>
              Cara Kami Menggunakan Data
            </h2>
            <div className="space-y-3 text-sm sm:text-base text-foreground/80 leading-relaxed pl-8">
              <p>Data pribadi yang dikumpulkan digunakan semata-mata untuk:</p>
              <ul className="list-disc pl-5 space-y-1.5 marker:text-brand-600">
                <li>Membuat, memelihara, dan mengamankan akun Anda, termasuk proses autentikasi (login) dan pemulihan kata sandi.</li>
                <li>Menampilkan tagihan iuran, memproses transaksi pembayaran, dan memverifikasi bukti pembayaran IPL.</li>
                <li>Memproses laporan tamu, keluhan warga, permohonan izin renovasi, pembuatan surat pengantar, dan reservasi fasilitas.</li>
                <li>Mengirimkan notifikasi operasional terkait layanan, pengingat jatuh tempo, dan maklumat resmi dari Pengelola.</li>
                <li>Menampilkan informasi promosi internal lingkungan dan mengukur jumlah kliknya secara statistik umum.</li>
                <li>Memastikan kompatibilitas teknis versi Aplikasi, memperbaiki kendala (bug), dan meningkatkan kinerja sistem.</li>
                <li>Mencegah penyalahgunaan sistem, tindak kecurangan, serta memenuhi kepatuhan regulasi hukum yang berlaku di Indonesia.</li>
              </ul>
              <div className="p-3.5 rounded-xl bg-brand-50 border border-brand-200 font-semibold text-brand-900 text-sm">
                Kami menegaskan bahwa kami tidak menjual, menyewakan, atau memperdagangkan data pribadi Anda kepada pihak mana pun untuk tujuan pemasaran pihak ketiga.
              </div>
            </div>
          </section>

          {/* 5. Dasar Pemrosesan Data */}
          <section id="section-5" className="mb-10 scroll-mt-24">
            <h2 className="text-lg sm:text-xl font-bold text-foreground mb-3 flex items-center gap-2">
              <span className="flex items-center justify-center h-6 w-6 rounded-full bg-brand-700 text-white text-xs font-bold">5</span>
              Dasar Pemrosesan Data
            </h2>
            <div className="text-sm sm:text-base text-foreground/80 leading-relaxed pl-8 space-y-2">
              <p>
                Sesuai ketentuan UU Pelindungan Data Pribadi (UU PDP No. 27/2022), kami memproses data Anda berdasarkan satu atau lebih dasar hukum yang sah:
              </p>
              <ul className="list-disc pl-5 space-y-1 marker:text-brand-600">
                <li><strong>Pemenuhan perjanjian:</strong> menjalankan layanan pengelolaan iuran dan perumahan yang disepakati antara Anda dan Pengelola.</li>
                <li><strong>Persetujuan yang eksplisit:</strong> persetujuan yang Anda berikan saat mengaktifkan izin notifikasi atau mengunggah berkas.</li>
                <li><strong>Kewajiban hukum:</strong> kepatuhan terhadap kewajiban pencatatan pembukuan, perizinan, atau instruksi aparat hukum yang berwenang.</li>
                <li><strong>Kepentingan yang sah (legitimate interests):</strong> menjaga stabilitas, keamanan server, dan mutu layanan Aplikasi.</li>
              </ul>
            </div>
          </section>

          {/* 6. Berbagi Data dengan Pihak Lain */}
          <section id="section-6" className="mb-10 scroll-mt-24">
            <h2 className="text-lg sm:text-xl font-bold text-foreground mb-3 flex items-center gap-2">
              <span className="flex items-center justify-center h-6 w-6 rounded-full bg-brand-700 text-white text-xs font-bold">6</span>
              Berbagi Data dengan Pihak Lain (Pihak Ketiga)
            </h2>
            <div className="space-y-3 text-sm sm:text-base text-foreground/80 leading-relaxed pl-8">
              <p>Data pribadi Anda hanya dibagikan secara ketat sebatas yang diperlukan untuk menyediakan layanan, yaitu kepada:</p>
              <div className="space-y-3">
                <div className="p-3 rounded-xl border border-slate-200 bg-white">
                  <h4 className="font-bold text-foreground text-sm">1. Pengelola Perumahan dan Petugas yang Ditunjuk</h4>
                  <p className="text-xs text-foreground/75 mt-0.5">
                    Pengurus kompleks, staf keuangan, dan petugas keamanan lapangan (misalnya verifikasi laporan tamu dan nomor kendaraan) sesuai dengan wewenang operasionalnya.
                  </p>
                </div>

                <div className="p-3 rounded-xl border border-slate-200 bg-white">
                  <h4 className="font-bold text-foreground text-sm">2. Penyedia Layanan Pembayaran Resmi (DOKU)</h4>
                  <p className="text-xs text-foreground/75 mt-0.5">
                    Untuk pemrosesan pembayaran elektronik (Virtual Account, QRIS, gerai ritel, kartu). Seluruh data instrumen keuangan diproses langsung oleh payment gateway berizin Bank Indonesia (DOKU) melalui koneksi terenkripsi PCI-DSS dan tidak disimpan oleh Aplikasi IPL.
                  </p>
                </div>

                <div className="p-3 rounded-xl border border-slate-200 bg-white">
                  <h4 className="font-bold text-foreground text-sm">3. Google Firebase Cloud Messaging (FCM)</h4>
                  <p className="text-xs text-foreground/75 mt-0.5">
                    Layanan infrastruktur Google untuk menghantarkan pesan notifikasi push ke perangkat Android pengguna secara andal.
                  </p>
                </div>

                <div className="p-3 rounded-xl border border-slate-200 bg-white">
                  <h4 className="font-bold text-foreground text-sm">4. Penyedia Infrastruktur Server &amp; Cloud</h4>
                  <p className="text-xs text-foreground/75 mt-0.5">
                    Penyedia layanan hosting dan basis data aman yang mengoperasikan server aplikasi dengan standar keamanan tinggi.
                  </p>
                </div>

                <div className="p-3 rounded-xl border border-slate-200 bg-white">
                  <h4 className="font-bold text-foreground text-sm">5. Aparat Penegak Hukum atau Instansi Berwenang</h4>
                  <p className="text-xs text-foreground/75 mt-0.5">
                    Hanya jika diwajibkan oleh proses hukum formal, peraturan perundang-undangan yang berlaku, atau penetapan pengadilan di Republik Indonesia.
                  </p>
                </div>
              </div>
              <p className="text-xs text-foreground/70">
                Seluruh mitra pihak ketiga terikat kontrak kerahasiaan dan hanya berhak memproses data sesuai instruksi layanan resmi kami.
              </p>
            </div>
          </section>

          {/* 7. Penyimpanan dan Keamanan Data */}
          <section id="section-7" className="mb-10 scroll-mt-24">
            <h2 className="text-lg sm:text-xl font-bold text-foreground mb-3 flex items-center gap-2">
              <span className="flex items-center justify-center h-6 w-6 rounded-full bg-brand-700 text-white text-xs font-bold">7</span>
              Penyimpanan dan Keamanan Data
            </h2>
            <div className="space-y-3 text-sm sm:text-base text-foreground/80 leading-relaxed pl-8">
              <ul className="list-disc pl-5 space-y-1.5 marker:text-brand-600">
                <li><strong>Enkripsi Transport (HTTPS / TLS):</strong> Seluruh data yang dikirimkan antara aplikasi di ponsel Anda dan server kami dienkripsi menggunakan protokol HTTPS modern.</li>
                <li><strong>Penyimpanan Sesi Aman:</strong> Kredensial token autentikasi disimpan di ruang penyimpanan terenkripsi perangkat (Android Keystore / Secure Storage) dan memiliki masa kadaluarsa otomatis.</li>
                <li><strong>Akses Terbatas:</strong> Hak akses terhadap basis data dibatasi dengan prinsip hak istimewa paling rendah (least privilege) hanya kepada tim teknis berwenang.</li>
              </ul>
              <p className="text-xs sm:text-sm text-foreground/70">
                Meskipun kami menerapkan langkah pengamanan teknis dan organisasional yang wajar, tidak ada transmisi data internet atau penyimpanan elektronik yang dapat dijamin 100% bebas dari segala risiko. Apabila terjadi kegagalan pelindungan data pribadi, kami akan memberitahukan kepada Anda dan otoritas pengawas sesuai tata cara dalam peraturan perundang-undangan.
              </p>
            </div>
          </section>

          {/* 8. Jangka Waktu Penyimpanan */}
          <section id="section-8" className="mb-10 scroll-mt-24">
            <h2 className="text-lg sm:text-xl font-bold text-foreground mb-3 flex items-center gap-2">
              <span className="flex items-center justify-center h-6 w-6 rounded-full bg-brand-700 text-white text-xs font-bold">8</span>
              Jangka Waktu Penyimpanan Data (Retensi)
            </h2>
            <div className="space-y-2 text-sm sm:text-base text-foreground/80 leading-relaxed pl-8">
              <p>
                Kami menyimpan data pribadi Anda selama akun Anda tetap aktif atau selama diperlukan untuk menyediakan layanan kepada Pengelola dan warga.
              </p>
              <p>
                Data pencatatan transaksi keuangan, kwitansi, dan riwayat pembayaran dapat disimpan hingga jangka waktu yang diwajibkan oleh ketentuan hukum perpajakan dan akuntansi di Indonesia. Setelah jangka waktu tersebut berakhir atau tujuan pemrosesan telah terpenuhi, data akan dihapus secara permanen atau dianonimkan sehingga tidak dapat dikaitkan lagi dengan Anda.
              </p>
            </div>
          </section>

          {/* 9. Hak Anda & Mekanisme Penghapusan Akun & Data (Account Deletion Policy - Google Play) */}
          <section id="hapus-akun" className="mb-10 scroll-mt-28">
            <span id="section-9" className="sr-only" />
            <span id="delete-account" className="sr-only" />
            <h2 className="text-lg sm:text-xl font-bold text-foreground mb-3 flex items-center gap-2">
              <span className="flex items-center justify-center h-6 w-6 rounded-full bg-brand-700 text-white text-xs font-bold">9</span>
              Hak Anda &amp; Tata Cara Penghapusan Akun dan Data
            </h2>

            <div className="space-y-4 text-sm sm:text-base text-foreground/80 leading-relaxed pl-8">
              <p>Sesuai peraturan perundang-undangan pelindungan data pribadi di Indonesia, Anda memiliki hak untuk:</p>
              <ul className="list-disc pl-5 space-y-1 text-foreground/75 text-sm marker:text-brand-600">
                <li>Mengakses dan memperoleh salinan data pribadi Anda yang tersimpan di sistem kami.</li>
                <li>Memperbarui atau membetulkan data yang keliru (sebagian data profil dan anggota keluarga dapat disunting langsung di menu <em>Akun &amp; Data Keluarga</em> pada Aplikasi).</li>
                <li><strong>Meminta penghapusan akun serta seluruh data pribadi Anda</strong> dari sistem.</li>
                <li>Menarik kembali persetujuan pemrosesan yang sebelumnya telah diberikan.</li>
                <li>Mengajukan keberatan atas pemrosesan data tertentu.</li>
              </ul>

              {/* Dedicated Google Play Compliant Account Deletion Box */}
              <div
                id="box-hapus-akun"
                className="p-5 sm:p-6 rounded-2xl bg-amber-50/70 border border-amber-300 text-amber-950 transition-all duration-500 shadow-sm"
              >
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 font-bold text-base sm:text-lg text-amber-900">
                    <Trash2 className="h-5 w-5 text-amber-700" />
                    Mekanisme Permohonan Penghapusan Akun &amp; Data Pribadi
                  </div>
                  <button
                    type="button"
                    onClick={copyDeletionUrl}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-900 border border-amber-300 transition"
                    title="Salin tautan langsung /privacy-policy#hapus-akun untuk Google Play"
                  >
                    {copiedDeletionLink ? (
                      <>
                        <Check className="h-3.5 w-3.5 text-emerald-600" />
                        Tautan Disalin!
                      </>
                    ) : (
                      <>
                        <Copy className="h-3.5 w-3.5" />
                        Salin URL Khusus Hapus Akun
                      </>
                    )}
                  </button>
                </div>
                <p className="text-xs sm:text-sm text-amber-900/90 leading-relaxed mb-4">
                  Sesuai kebijakan Google Play Store dan UU PDP, setiap pengguna berhak mengajukan penghapusan akun beserta data yang terkait secara permanen. Anda dapat mengajukan permintaan melalui salah satu kanal berikut:
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                  <a
                    href="mailto:nuratech.ds@gmail.com?subject=Permintaan%20Penghapusan%20Akun%20Aplikasi%20IPL&body=Halo%20Tim%20PT%20Nuratech%20Digital%20Nusantara,%0A%0ASaya%20ingin%20mengajukan%20permohonan%20penghapusan%20akun%20dan%20data%20pribadi%20saya%20dari%20Aplikasi%20IPL.%0A%0ABerikut%20data%20akun%20saya:%0A-%20Nama%20Lengkap:%20%0A-%20Username%20/%20Email:%20%0A-%20Nama%20Perumahan:%20%0A-%20Kode%20Unit:%20%0A%0ATerima%20kasih."
                    className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white border border-amber-300 text-amber-900 font-semibold text-xs sm:text-sm hover:bg-amber-100 transition shadow-2xs"
                  >
                    <Mail className="h-4 w-4 text-amber-700" />
                    Kirim Permintaan via Email
                  </a>

                  <a
                    href="https://wa.me/6285703954618?text=Halo%20Admin%20IPL,%20saya%20ingin%20mengajukan%20penghapusan%20akun%20dan%20data%20aplikasi%20IPL.%0A%0ANama:%20%0AUsername/Email:%20%0ANama%20Perumahan:%20%0AKode%20Unit:%20"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-emerald-600 text-white font-semibold text-xs sm:text-sm hover:bg-emerald-700 transition shadow-2xs"
                  >
                    <MessageCircle className="h-4 w-4" />
                    Kirim Permintaan via WhatsApp
                  </a>
                </div>

                <div className="bg-white/80 p-3.5 rounded-xl border border-amber-200/80 text-xs text-amber-900">
                  <div className="flex items-center justify-between gap-2 mb-1.5 font-bold">
                    <span>Data yang Wajib Disertakan dalam Permintaan:</span>
                    <button
                      type="button"
                      onClick={copyDeletionTemplate}
                      className="text-amber-800 hover:text-amber-950 inline-flex items-center gap-1 text-[11px] underline font-medium"
                    >
                      {copiedTemplate ? <Check className="h-3 w-3 text-emerald-600" /> : <Copy className="h-3 w-3" />}
                      {copiedTemplate ? 'Format Disalin' : 'Salin Format'}
                    </button>
                  </div>
                  <ul className="list-disc pl-4 space-y-0.5 text-amber-800">
                    <li>Nama lengkap pemegang akun</li>
                    <li>Username atau alamat email yang terdaftar pada Aplikasi IPL</li>
                    <li>Nama kompleks perumahan / lingkungan dan kode unit hunian</li>
                  </ul>
                  <p className="mt-2 text-amber-900/80 italic">
                    <strong>Waktu Penyelesaian:</strong> Tim kami akan memverifikasi identitas Anda demi mencegah penyalahgunaan akun, dan memproses penghapusan paling lambat <strong>30 (tiga puluh) hari kerja</strong>. Data transaksi yang menurut undang-undang wajib disimpan untuk keperluan pembukuan hukum akan disimpan sesuai jangka waktu retensi yang diwajibkan lalu dimusnahkan.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* 10. Data Anak */}
          <section id="section-10" className="mb-10 scroll-mt-24">
            <h2 className="text-lg sm:text-xl font-bold text-foreground mb-3 flex items-center gap-2">
              <span className="flex items-center justify-center h-6 w-6 rounded-full bg-brand-700 text-white text-xs font-bold">10</span>
              Perlindungan Data Anak
            </h2>
            <div className="text-sm sm:text-base text-foreground/80 leading-relaxed pl-8 space-y-2">
              <p>
                Aplikasi IPL ditujukan dan dirancang untuk digunakan oleh pengguna dewasa (pengurus, kepala keluarga, warga dewasa, dan petugas lingkungan).
              </p>
              <p>
                Data anak di bawah umur hanya dapat tercatat sebagai anggota keluarga yang diinput secara sadar oleh orang tua atau wali yang sah, dan semata-mata dimanfaatkan untuk keperluan pendataan warga oleh Pengelola perumahan. Kami tidak dengan sengaja mengizinkan anak di bawah umur untuk mendaftar akun mandiri tanpa persetujuan orang tua atau wali.
              </p>
            </div>
          </section>

          {/* 11. Perubahan Kebijakan Privasi */}
          <section id="section-11" className="mb-10 scroll-mt-24">
            <h2 className="text-lg sm:text-xl font-bold text-foreground mb-3 flex items-center gap-2">
              <span className="flex items-center justify-center h-6 w-6 rounded-full bg-brand-700 text-white text-xs font-bold">11</span>
              Perubahan Kebijakan Privasi
            </h2>
            <div className="text-sm sm:text-base text-foreground/80 leading-relaxed pl-8 space-y-2">
              <p>
                Kami dapat meninjau dan memperbarui Kebijakan Privasi ini dari waktu ke waktu untuk menyesuaikan dengan pengembangan fitur baru pada Aplikasi, perubahan operasional, atau perubahan perundang-undangan.
              </p>
              <p>
                Setiap versi pembaruan akan dipublikasikan secara terbuka pada halaman ini dengan tanggal berlaku yang diperbarui. Apabila terdapat perubahan yang bersifat material atau mendasar, kami akan menyampaikan pemberitahuan melalui pemberitahuan dalam Aplikasi (in-app banner/notification) sebelum perubahan berlaku efektif.
              </p>
            </div>
          </section>

          {/* 12. Hubungi Kami */}
          <section id="section-12" className="mb-8 scroll-mt-24">
            <h2 className="text-lg sm:text-xl font-bold text-foreground mb-3 flex items-center gap-2">
              <span className="flex items-center justify-center h-6 w-6 rounded-full bg-brand-700 text-white text-xs font-bold">12</span>
              Hubungi Kami
            </h2>
            <div className="space-y-4 text-sm sm:text-base text-foreground/80 leading-relaxed pl-8">
              <p>
                Jika Anda memiliki pertanyaan, klarifikasi, atau permintaan hak data terkait Kebijakan Privasi ini, silakan menghubungi kami melalui:
              </p>

              <div className="p-5 rounded-2xl bg-brand-50 border border-brand-200 space-y-3 text-sm">
                <div className="font-bold text-brand-900 text-base">
                  PT Nuratech Digital Nusantara
                </div>
                <div className="text-foreground/80">
                  <span className="font-semibold text-foreground">Alamat Kantor:</span>
                  <br />
                  Menara Cakrawala, 12th Floor Unit 5A, Jakarta Pusat, DKI Jakarta 10340, Indonesia
                </div>
                <div className="flex flex-wrap gap-y-2 gap-x-6 pt-1">
                  <a
                    href="mailto:nuratech.ds@gmail.com"
                    className="inline-flex items-center gap-1.5 text-brand-700 hover:text-brand-900 font-semibold"
                  >
                    <Mail className="h-4 w-4" />
                    nuratech.ds@gmail.com
                  </a>
                  <a
                    href="https://wa.me/6285703954618"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-brand-700 hover:text-brand-900 font-semibold"
                  >
                    <MessageCircle className="h-4 w-4" />
                    WhatsApp: +62 857-0395-4618
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* Web Cookie Reset Tool (Kept for Web Visitors) */}
          {!isAppView && (
            <footer className="border-t border-border pt-8 mt-12">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
                    <Cookie className="h-4 w-4 text-brand-700" />
                    Preferensi Cookie Situs Web
                  </h3>
                  <p className="text-xs text-foreground/60 mt-0.5">
                    Pengaturan cookie hanya berlaku pada browser situs landing page dan tidak memengaruhi aplikasi Android.
                  </p>
                </div>
                <Button
                  onClick={resetCookieConsent}
                  variant="outline"
                  size="sm"
                  className="border-brand-200 hover:bg-brand-50 text-xs shrink-0"
                >
                  Reset Cookie Browser
                </Button>
              </div>
            </footer>
          )}
        </motion.article>
      </div>
    </div>
  );
};
